import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import { SelectFormInput, TextFormInput } from "src/components/form";
import { TrialFinanceBreadcrumbs } from "src/helpers/breadcrumbHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "src/helpers/lookupHelper";
import useNavigate from "src/hooks/useNavigate";
import { RegistrationFormComponent } from "src/interfaces/forms/IRegistrationComponent";
import { ITrialFinanceReduxFormState } from "src/reducers/reactReduxForms/trialFinance";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialFinanceActions from "../../../actions/trialFinance";
import { NotificationAlert } from "../../../components/common/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { TrialSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import "./TrialFinancePage.scss";

interface TrialFinancePageProps {
    form: Dtos.TrialFinance & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ITrialFinanceReduxFormState;
    trialFinance: Dtos.TrialFinance;
    lookups: Dtos.Lookup[];
    loadingTrialFinance: boolean;
    loadTrialFinanceSuccess: boolean;
    loadTrialFinanceFailure: boolean;
    loadTrialFinanceErrorCode: string;
    savingTrialFinance: boolean;
    saveTrialFinanceSuccess: boolean;
    saveTrialFinanceFailure: boolean;
    validatingTrialFinance: boolean;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialFinance: TrialFinanceActions.ITrialFinanceClearActionCreator;
    loadTrialFinance: TrialFinanceActions.ITrialFinanceLoadByTrialIdActionCreator;
    saveTrialFinance: TrialFinanceActions.ITrialFinanceSaveActionCreator;
    validateTrialFinance: TrialFinanceActions.ITrialFinanceValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.trialFinance";

const TrialFinancePage: FC<TrialFinancePageProps> = ({
    form,
    formState,
    formProperties,
    trialFinance,
    lookups,
    loadingTrialFinance,
    loadTrialFinanceFailure,
    loadTrialFinanceErrorCode,
    savingTrialFinance,
    saveTrialFinanceSuccess,
    saveTrialFinanceFailure,
    validatingTrialFinance,
    validationFailures,
    permissions,
    clearTrialFinance,
    loadTrialFinance,
    saveTrialFinance,
    validateTrialFinance,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const trialIdNumber = parseInt(trialId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();


    // Load Trial Finance
    useEffect(() => {
        if (trialIdNumber && !trialFinance) {
            setBreadcrumbs(TrialFinanceBreadcrumbs(trialIdNumber));
            setDrawerItems(TrialSubMenu(trialIdNumber, permissions));

            loadTrialFinance(trialIdNumber);
        } else if (trialFinance && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        trialIdNumber,
        trialFinance,
        permissions,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        loadTrialFinance,
        TrialFinanceBreadcrumbs,
        setFormInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialFinanceFailure) {
            navigate(`/error/${loadTrialFinanceErrorCode}`);
        }
    }, [loadTrialFinanceFailure, loadTrialFinanceErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialIdNumber && trialFinance?.trial) {
            setBreadcrumbs(
                TrialFinanceBreadcrumbs(
                    trialIdNumber,
                    trialFinance.trial?.displayName
                )
            );
        }
    }, [trialIdNumber, trialFinance, setBreadcrumbs, TrialFinanceBreadcrumbs]);

    // Reload trial finance after saving
    useEffect(() => {
        if (!savingTrialFinance && saveTrialFinanceSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial finance saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                const url = `/trials`;
                navigate(url);
            } else if (!trialIdNumber) {
                const url = `/trials/${trialIdNumber}/finance`;
                navigate(url);
                clearTrialFinance();
            }
        }
    }, [
        savingTrialFinance,
        saveTrialFinanceSuccess,
        trialIdNumber,
        navigate,
        saveAndReturn,
        clearTrialFinance,
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTrialFinance && saveTrialFinanceFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingTrialFinance, saveTrialFinanceSuccess]);

    // Cleanup effect to clear the trial finance on component unmount
    useEffect(() => {
        return () => {
            clearTrialFinance();
            resetForm(reduxFormName);
        };
    }, [clearTrialFinance, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialFinance = {} as Dtos.TrialFinance;

            if (!data.trialId) {
                changes.trialId = trialIdNumber;
            }

            await saveTrialFinance(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialFinance) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        const url = "/trials";
        navigate(url);
    };

    const loadReduxForm = () => {
        let extraData: Dtos.TrialFinance = {} as Dtos.TrialFinance;

        loadForm(reduxFormName, Object.assign({ ...trialFinance }, extraData));
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialFinance = {} as Dtos.TrialFinance;

            if (form && hasBeenSubmitted && !validatingTrialFinance) {
                await validateTrialFinance(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                trialIdNumber,
                null,
                permissions,
                Dtos.Permission.TrialFinanceUpdate,
                Dtos.Permission.TrialFinanceCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialFinance ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "trialFinance",
                                        {
                                            quickbooksAccountId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "TrialFinance",
                                                                "quickbooksAccountId",
                                                                lookups
                                                            ),
                                                            form.quickbooksAccountId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "trialFinance",
                                        {
                                            quickbooksClassId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "TrialFinance",
                                                                "quickbooksClassId",
                                                                lookups
                                                            ),
                                                            form.quickbooksClassId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialFinance",
                                        {
                                            ctaClauseReference: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "trialFinance",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        permissions,
        savingTrialFinance,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        trialFinance,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialGroupLabView}
            loading={loadingTrialFinance || savingTrialFinance}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TrialFinancePageProps> => {
    let trialFinance: Dtos.TrialFinance | undefined = !(
        state.trialFinance.formData instanceof Array
    )
        ? state.trialFinance.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialFinance: trialFinance!,
        loadingTrialFinance:
            state.trialFinance.loadState &&
            state.trialFinance.loadState.status == RequestState.Pending,
        loadTrialFinanceSuccess:
            state.trialFinance.loadState &&
            state.trialFinance.loadState.status == RequestState.Success,
        loadTrialFinanceFailure:
            state.trialFinance.loadState &&
            state.trialFinance.loadState.status == RequestState.Failure,
        loadTrialFinanceErrorCode: state.trialFinance.loadState.errorCode,
        savingTrialFinance:
            state.trialFinance.saveState &&
            state.trialFinance.saveState.status == RequestState.Pending,
        saveTrialFinanceSuccess:
            state.trialFinance.saveState &&
            state.trialFinance.saveState.status == RequestState.Success,
        saveTrialFinanceFailure:
            state.trialFinance.saveState &&
            state.trialFinance.saveState.status == RequestState.Failure,
        validatingTrialFinance:
            state.trialFinance.validateState &&
            state.trialFinance.validateState.status == RequestState.Pending,
        user: state.user.data,
        permissions: permissions,
        lookups: state.trialFinance.lookups,
        form: state.reduxForms.trialFinance,
        formState: state.trialFinance.formState,
        formProperties: state.trialFinance.formProperties,
        reduxFormState: state.reduxForms.formState.trialFinance,
        validationFailures: trialFinance
            ? state.trialFinance.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialFinancePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialFinance: bindActionCreators(
            TrialFinanceActions.LoadTrialFinanceByTrialId,
            dispatch
        ),
        clearTrialFinance: bindActionCreators(
            TrialFinanceActions.Clear,
            dispatch
        ),
        saveTrialFinance: bindActionCreators(
            TrialFinanceActions.SaveTrialFinance,
            dispatch
        ),
        validateTrialFinance: bindActionCreators(
            TrialFinanceActions.ValidateTrialFinance,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialFinancePage);
